/**
 * Ticket status and urgency display helpers.
 * Uses map lookups for O(1) performance.
 */

// ─── Urgency Badge Color (custom class suffix) ─────────────
const URGENCY_COLOR_MAP = { critical: 'danger', high: 'warning', medium: 'primary', low: 'info' };
export const getUrgencyColor = (urgency) => URGENCY_COLOR_MAP[urgency] || 'secondary';

// ─── Status Badge Color (custom class suffix) ───────────────
const STATUS_COLOR_MAP = { open: 'success', in_progress: 'primary', resolved: 'info', closed: 'secondary' };
export const getStatusColor = (status) => STATUS_COLOR_MAP[status] || 'light';

// ─── Urgency Icons ──────
const URGENCY_ICON_MAP = { critical: '🔥', high: '⚠️', medium: '📋', low: '📝' };
export const getUrgencyIcon = (urgency) => URGENCY_ICON_MAP[urgency] || '📌';

// ─── Status Icons ───────
const STATUS_ICON_MAP = { open: '🟢', in_progress: '🟡', resolved: '🔵', closed: '⚫' };
export const getStatusIcon = (status) => STATUS_ICON_MAP[status] || '⚪';

// ─── Urgency Badge Classes (user-side) ──────────────────────
const URGENCY_BADGE_MAP = { low: 'badge badge-info', medium: 'badge badge-primary', high: 'badge badge-warning', critical: 'badge badge-danger' };
export const getUrgencyBadgeClass = (urgency) => URGENCY_BADGE_MAP[urgency] || 'badge badge-secondary';

// ─── Status Badge Classes (user-side) ───────────────────────
const STATUS_BADGE_MAP = { open: 'badge badge-success', in_progress: 'badge badge-warning', resolved: 'badge badge-info', closed: 'badge badge-secondary' };
export const getStatusBadgeClass = (status) => STATUS_BADGE_MAP[status] || 'badge badge-light';

// ─── Search Results Status Badge Classes ────────────────────
const RESULTS_STATUS_BADGE_MAP = { available: 'badge badge-success', expired: 'badge badge-secondary', no_results: 'badge badge-light' };
export const getResultsStatusBadgeClass = (status) => RESULTS_STATUS_BADGE_MAP[status] || 'badge badge-light';

// ─── Website Icon (lucide-react component name) ────────────
const WEBSITE_ICON_MAP = { amazon: 'ShoppingBag', ebay: 'ShoppingCart', flipkart: 'Store', shopify: 'ShoppingCart' };
export const getWebsiteIcon = (website) => WEBSITE_ICON_MAP[website?.toLowerCase()] || 'Globe';

// ─── Chart Colors ───────
export const STATUS_CHART_COLORS = {
  open: 'rgba(40, 167, 69, 0.9)',
  in_progress: 'rgba(0, 123, 255, 0.9)',
  resolved: 'rgba(23, 162, 184, 0.9)',
  closed: 'rgba(108, 117, 125, 0.9)',
};

export const STATUS_BORDER_COLORS = {
  open: '#28a745',
  in_progress: '#007bff',
  resolved: '#17a2b8',
  closed: '#6c757d',
};

export const URGENCY_BAR_COLORS = {
  critical: 'rgba(220, 53, 69, 0.7)',
  high: 'rgba(255, 193, 7, 0.7)',
  medium: 'rgba(0, 123, 255, 0.7)',
  low: 'rgba(23, 162, 184, 0.7)',
};

// ─── Jira Sync Status Badge Classes ──────────────────────────
const JIRA_SYNC_BADGE_MAP = {
  pending: 'badge badge-secondary',
  processing: 'badge badge-primary',
  synced: 'badge badge-success',
  needs_review: 'badge badge-warning',
  failed: 'badge badge-danger',
  jira_issue_missing: 'badge badge-danger',
};
export const getJiraSyncBadgeClass = (status) => JIRA_SYNC_BADGE_MAP[status] || 'badge badge-light';

const JIRA_SYNC_LABEL_MAP = {
  pending: 'Not synced yet',
  processing: 'Syncing to Jira…',
  synced: 'Synced to Jira',
  needs_review: 'Synced - needs review',
  failed: 'Jira sync failed',
  jira_issue_missing: 'Jira issue deleted',
};
export const getJiraSyncLabel = (status) => JIRA_SYNC_LABEL_MAP[status] || 'Unknown';

// ─── Status/Urgency Label Formatting ────────────────────────
/** Replaces underscores with spaces and uppercases. e.g. 'in_progress' → 'IN PROGRESS' */
export const formatStatusLabel = (value) => value?.replace(/_/g, ' ').toUpperCase() || '';

/** Title-cases a status. e.g. 'in_progress' → 'In Progress' */
export const formatStatusTitle = (value) =>
  value?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || '';